"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback } from "react";
import { useAuth } from "@/components/auth/Auth";
import { Section, TraitField } from "./traits";
import { getFormSections } from "./traits/schema";
import type { TraitsData } from "./submission/types/TraitsData";
interface MemesArtSubmissionTraitsProps {
  readonly traits: TraitsData;
  readonly setTraits: (traits: Partial<TraitsData>) => void;
  readonly showTitle?: boolean | undefined;
  readonly validationErrors?: Record<keyof TraitsData, string | null> | undefined;
  readonly onFieldBlur?: ((field: keyof TraitsData) => void) | undefined;
  readonly readOnlyOverrides?: Partial<Record<keyof TraitsData, boolean>>;
  readonly showRequiredMarkers?: boolean | undefined;
  readonly size?: "default" | "sm" | undefined;
}

/**
 * MemesArtSubmissionTraits - Component for managing all artwork trait fields
 *
 * Simplified implementation with direct updates to the central state
 */
const MemesArtSubmissionTraits: React.FC<MemesArtSubmissionTraitsProps> = t0 => {
  const $ = _c(39);
  const {
    traits,
    setTraits,
    showTitle: t1,
    validationErrors: t2,
    onFieldBlur,
    readOnlyOverrides,
    showRequiredMarkers: t3,
    size
  } = t0;
  const showTitle = t1 === undefined ? true : t1;
  let t4;
  if ($[0] !== t2) {
    t4 = t2 === undefined ? {} : t2;
    $[0] = t2;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const validationErrors = t4;
  const showRequiredMarkers = t3 === undefined ? false : t3;
  const {
    connectedProfile
  } = useAuth();
  const userProfile = connectedProfile?.handle;
  let t5;
  if ($[2] !== setTraits) {
    t5 = (field, value) => {
      setTraits({
        [field]: value
      });
    };
    $[2] = setTraits;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const updateText = t5;
  let t6;
  if ($[4] !== setTraits) {
    t6 = (field_0, value_0) => {
      setTraits({
        [field_0]: value_0
      });
    };
    $[4] = setTraits;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const updateBoolean = t6;
  let t7;
  if ($[6] !== setTraits) {
    t7 = (field_1, value_1) => {
      setTraits({
        [field_1]: value_1
      });
    };
    $[6] = setTraits;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const updateNumber = t7;
  let t8;
  if ($[8] !== readOnlyOverrides || $[9] !== userProfile) {
    t8 = getFormSections(userProfile, readOnlyOverrides);
    $[8] = readOnlyOverrides;
    $[9] = userProfile;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const formSections = t8;
  let t9;
  if ($[11] !== showTitle) {
    t9 = showTitle && <h2 className="tw-mb-5 tw-text-base tw-font-semibold tw-tracking-tight tw-text-iron-100">Artwork Traits</h2>;
    $[11] = showTitle;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== formSections || $[14] !== onFieldBlur || $[15] !== readOnlyOverrides || $[16] !== showRequiredMarkers || $[17] !== size || $[18] !== traits || $[19] !== updateBoolean || $[20] !== updateNumber || $[21] !== updateText || $[22] !== validationErrors) {
    let t11;
    if ($[24] !== onFieldBlur || $[25] !== readOnlyOverrides || $[26] !== showRequiredMarkers || $[27] !== size || $[28] !== traits || $[29] !== updateBoolean || $[30] !== updateNumber || $[31] !== updateText || $[32] !== validationErrors) {
      t11 = section => {
        const sectionKey = section.title || section.fields.map(_temp).join("|");
        const renderField = field_3 => {
          const readOnlyOverride = readOnlyOverrides?.[field_3.field];
          const traitFieldOverrideProps = readOnlyOverride === undefined ? {} : {
            readOnlyOverride: Boolean(readOnlyOverride)
          };
          return <TraitField key={field_3.field} definition={field_3} {...traitFieldOverrideProps} traits={traits} updateText={updateText} updateNumber={updateNumber} updateBoolean={updateBoolean} error={validationErrors[field_3.field]} onBlur={onFieldBlur ? () => onFieldBlur(field_3.field) : undefined} showRequiredMarkers={showRequiredMarkers} size={size} />;
        };
        if (section.title === "Basic Information") {
          const artistField = section.fields.find(_temp2);
          const profileField = section.fields.find(_temp3);
          const remainingFields = section.fields.filter(_temp4);
          return <Section key={sectionKey} title={section.title}><div className="tw-flex tw-flex-col tw-gap-x-6 tw-gap-y-8"><div className="tw-grid tw-grid-cols-1 tw-gap-x-5 tw-gap-y-8 sm:tw-grid-cols-2">{artistField && renderField(artistField)}{profileField && renderField(profileField)}</div>{remainingFields.map(field_4 => renderField(field_4))}</div></Section>;
        }
        if (section.title === "Card Points") {
          return <Section key={sectionKey} title={section.title}><div className="tw-grid tw-grid-cols-2 tw-gap-4 sm:tw-grid-cols-4">{section.fields.map(field_5 => renderField(field_5))}</div></Section>;
        }
        if (section.title === "Card Attributes") {
          return <Section key={sectionKey} title={section.title}><div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">{section.fields.map(field_6 => renderField(field_6))}</div></Section>;
        }
        return <Section key={sectionKey} title={section.title}><div className="tw-grid tw-grid-cols-1 tw-gap-x-5 tw-gap-y-8 sm:tw-grid-cols-2">{section.fields.map(field_7 => renderField(field_7))}</div></Section>;
      };
      $[24] = onFieldBlur;
      $[25] = readOnlyOverrides;
      $[26] = showRequiredMarkers;
      $[27] = size;
      $[28] = traits;
      $[29] = updateBoolean;
      $[30] = updateNumber;
      $[31] = updateText;
      $[32] = validationErrors;
      $[33] = t11;
    } else {
      t11 = $[33];
    }
    t10 = formSections.map(t11);
    $[13] = formSections;
    $[14] = onFieldBlur;
    $[15] = readOnlyOverrides;
    $[16] = showRequiredMarkers;
    $[17] = size;
    $[18] = traits;
    $[19] = updateBoolean;
    $[20] = updateNumber;
    $[21] = updateText;
    $[22] = validationErrors;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[34] !== t10) {
    t11 = <div className="tw-flex tw-flex-col tw-gap-y-10">{t10}</div>;
    $[34] = t10;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t11 || $[37] !== t9) {
    t12 = <div className="tw-flex tw-flex-col">{t9}{t11}</div>;
    $[36] = t11;
    $[37] = t9;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  return t12;
};

// Use React.memo to prevent unnecessary rerenders
export default React.memo(MemesArtSubmissionTraits);
function _temp(field_2) {
  return field_2.field;
}
function _temp2(f) {
  return f.field === "artist";
}
function _temp3(f_0) {
  return f_0.field === "seizeArtistProfile";
}
function _temp4(f_1) {
  return f_1.field !== "artist" && f_1.field !== "seizeArtistProfile";
}